import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../../../supabaseClient';
import {
  Grid, TextField, FormControl, InputLabel, Select, MenuItem,
  Typography, Box, Card, CardContent, Divider, Autocomplete,
  Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Chip, Avatar, Dialog, DialogTitle, DialogContent,
  DialogActions, Button, InputAdornment, useTheme, alpha, IconButton
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import {
  Business as BusinessIcon,
  Work as WorkIcon,
  LocationOn as LocationOnIcon,
  Schedule as ScheduleIcon,
  AccountTree as AccountTreeIcon,
  Search as SearchIcon,
  Add as AddIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { usePermissions } from '../../../utils/permissions';
import JobDescriptionMarkdown from './JobDescriptionMarkdown';

// Isolated input component that doesn't trigger parent re-renders
const IsolatedInput = React.memo(({ field, value, onChange, placeholder, multiline = false, rows = 1, type = 'text', style = {}, disabled = false }) => {
  const [localValue, setLocalValue] = useState(value);
  const inputRef = useRef(null);

  useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleChange = (e) => {
    if (disabled) return;
    const newValue = e.target.value;
    setLocalValue(newValue);
  };

  const handleBlur = () => {
    if (disabled) return;
    onChange(field, localValue);
  };

  const baseStyle = {
    width: '100%',
    border: 'none',
    outline: 'none',
    background: '#f8f9fa',
    padding: '8px 12px',
    fontSize: '0.875rem',
    fontFamily: 'inherit',
    color: 'inherit',
    borderRadius: '4px',
    marginLeft: '8px'
  };

  if (multiline) {
    return (
      <textarea
        ref={inputRef}
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        placeholder={placeholder}
        rows={rows}
        disabled={disabled}
        style={{
          ...baseStyle,
          resize: 'none',
          ...style
        }}
      />
    );
  }

  return (
    <input
      ref={inputRef}
      type={type}
      value={localValue}
      onChange={handleChange}
      onBlur={handleBlur}
      placeholder={placeholder}
      disabled={disabled}
      style={{
        ...baseStyle,
        ...style
      }}
    />
  );
});

const DAYS_OF_WEEK = [
  'Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'
];

const EMPLOYMENT_TYPES = [
  'employee',
  'student', 
  'freelancer',
  'contractor',
  'intern',
  'consultant'
];

const WORKING_HOURS_OPTIONS = [
  'Standard 40 hours/week',
  'Part-time 20 hours/week',
  'Part-time 30 hours/week',
  'Flexible hours',
  'Custom schedule'
];

const TIMEZONES = [
  'America/New_York',
  'America/Chicago', 
  'America/Denver',
  'America/Los_Angeles',
  'America/Phoenix',
  'America/Anchorage',
  'Pacific/Honolulu',
  'UTC'
];

const WORK_LOCATIONS = [
  'Office',
  'Remote',
  'Hybrid',
  'Warehouse',
  'Factory',
  'Client Site',
  'Field Work',
  'Travel',
  'Other'
];

export default function WorkTab({ 
  formData, 
  onInputChange, 
  remoteSchedule, 
  onRemoteScheduleChange,
  departments,
  employees,
  companyId 
}) {
  const theme = useTheme();
  const { canEditWorkInformation } = usePermissions();
  const [locations, setLocations] = useState([]);
  const [departmentEmployees, setDepartmentEmployees] = useState([]);
  
  // Local state for job description to prevent re-renders on every keystroke
  const [localJobDescription, setLocalJobDescription] = useState(formData.job_description || '');

  // Department selection dialog states
  const [departmentSelectionDialogOpen, setDepartmentSelectionDialogOpen] = useState(false);
  const [departmentSearchTerm, setDepartmentSearchTerm] = useState('');
  const [dialogSelectedDepartment, setDialogSelectedDepartment] = useState(null);

  useEffect(() => {
    loadLocations();
  }, [companyId]);

  // Update local job description when formData changes
  useEffect(() => {
    setLocalJobDescription(formData.job_description || '');
  }, [formData.job_description]);

  useEffect(() => {
    if (formData.department_id) {
      loadDepartmentEmployees(formData.department_id);
    } else {
      setDepartmentEmployees([]);
    }
  }, [formData.department_id, employees]);

  const loadLocations = async () => {
    try {
      const { data, error } = await supabase
        .from('locations')
        .select('*')
        .eq('company_id', companyId)
        .eq('is_active', true)
        .order('name');

      if (error) throw error;
      setLocations(data || []);
    } catch (error) {
      console.error('Error loading locations:', error);
    }
  };

  const loadDepartmentEmployees = (departmentId) => {
    // Get all employees in the same department who could be managers
    const deptEmployees = employees.filter(emp => 
      emp.department_id === departmentId && emp.id !== formData.id
    );
    setDepartmentEmployees(deptEmployees);
  };

  const handleRemoteScheduleChange = (dayIndex, field, value) => {
    const updatedSchedule = [...remoteSchedule];
    updatedSchedule[dayIndex] = {
      ...updatedSchedule[dayIndex],
      [field]: value
    };
    onRemoteScheduleChange(updatedSchedule);
  };

  // Get flattened departments for dropdown
  const getFlatDepartments = (depts, level = 0) => {
    let result = [];
    depts.forEach(dept => {
      result.push({
        ...dept,
        displayName: '  '.repeat(level) + dept.name,
        level
      });
      if (dept.children?.length) {
        result = result.concat(getFlatDepartments(dept.children, level + 1));
      }
    });
    return result;
  };

  const flatDepartments = getFlatDepartments(departments);

  // Department selection dialog handlers
  const handleOpenDepartmentSelectionDialog = () => {
    setDepartmentSelectionDialogOpen(true);
    setDepartmentSearchTerm('');
    setDialogSelectedDepartment(null);
  };

  const handleCloseDepartmentSelectionDialog = () => {
    setDepartmentSelectionDialogOpen(false);
    setDepartmentSearchTerm('');
    setDialogSelectedDepartment(null);
  };

  const handleDialogDepartmentSelect = (department) => {
    setDialogSelectedDepartment(department);
  };

  const handleConfirmDepartmentSelection = () => {
    if (dialogSelectedDepartment) {
      onInputChange('department_id', dialogSelectedDepartment.id);
      setDepartmentSelectionDialogOpen(false);
      setDepartmentSearchTerm('');
      setDialogSelectedDepartment(null);
    }
  };

  const getFilteredDepartmentsForSelection = () => {
    if (!departmentSearchTerm) return flatDepartments || [];
    
    const searchLower = departmentSearchTerm.toLowerCase();
    return (flatDepartments || []).filter(department => 
      department.name.toLowerCase().includes(searchLower)
    );
  };

  return (
    <Box sx={{ p: 4 }}>
      {/* Work Information Section */}
      <Card sx={{
        mb: 4,
        borderRadius: 3,
        border: `1px solid rgba(0, 0, 0, 0.08)`,
        background: 'white',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.06)',

      }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Box sx={{ 
              p: 1.5, 
              borderRadius: 2, 
              background: 'linear-gradient(rgba(25, 118, 210, 0.1), rgba(25, 118, 210, 0.05))',
              border: '1px solid rgba(25, 118, 210, 0.2)'
            }}>
              <WorkIcon sx={{ fontSize: 24, color: 'primary.main' }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 500, color: 'primary.main' }}>
              Work Information
            </Typography>
          </Box>
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
              <Typography sx={{ fontWeight: 500, width: 120, color: 'text.primary' }}>
                First Name *
              </Typography>
              <IsolatedInput 
                field="first_name"
                value={formData.first_name || ''}
                onChange={onInputChange}
                placeholder="John"
                disabled={!canEditWorkInformation()}
                style={{ opacity: canEditWorkInformation() ? 1 : 0.6, cursor: canEditWorkInformation() ? 'text' : 'not-allowed' }}
              />
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
              <Typography sx={{ fontWeight: 500, width: 120, color: 'text.primary' }}>
                Last Name *
              </Typography>
              <IsolatedInput 
                field="last_name"
                value={formData.last_name || ''}
                onChange={onInputChange}
                placeholder="Doe"
                disabled={!canEditWorkInformation()}
                style={{ opacity: canEditWorkInformation() ? 1 : 0.6, cursor: canEditWorkInformation() ? 'text' : 'not-allowed' }}
              />
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
              <Typography sx={{ fontWeight: 500, width: 120, color: 'text.primary' }}>
                Employee Number
              </Typography>
              <IsolatedInput 
                field="employee_number"
                value={formData.employee_number || ''}
                onChange={onInputChange}
                placeholder="EMP001"
                disabled={!canEditWorkInformation()}
                style={{ opacity: canEditWorkInformation() ? 1 : 0.6, cursor: canEditWorkInformation() ? 'text' : 'not-allowed' }}
              />
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
              <Typography sx={{ fontWeight: 500, width: 120, color: 'text.primary' }}>
                Department
              </Typography>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleOpenDepartmentSelectionDialog}
                disabled={!canEditWorkInformation()}
                sx={{
                  justifyContent: 'flex-start',
                  textAlign: 'left',
                  py: 1,
                  px: 2,
                  height: 40,
                  borderRadius: 2,
                  backgroundColor: '#f8f9fa',
                  border: `1px solid rgba(0,0,0,0.1)`,
                  color: formData.department_id ? 'text.primary' : 'text.secondary',
                  fontWeight: 500,
                  textTransform: 'none',
                  fontSize: '0.875rem',
                  marginLeft: '8px',
                  opacity: canEditWorkInformation() ? 1 : 0.6,
                  cursor: canEditWorkInformation() ? 'pointer' : 'not-allowed',
                  '&:hover': canEditWorkInformation() ? {
                    border: `1px solid rgba(0,0,0,0.2)`,
                    backgroundColor: '#f8f9fa'
                  } : {},
                  '&:disabled': {
                    backgroundColor: '#f8f9fa',
                    color: 'text.secondary'
                  }
                }}
              >
                {formData.department_id ? 
                  (() => {
                    const department = flatDepartments.find(d => d.id === formData.department_id);
                    return department ? department.displayName : 'Select Department';
                  })() :
                  'Select Department'
                }
              </Button>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
              <Typography sx={{ fontWeight: 500, width: 120, color: 'text.primary' }}>
                Job Position
              </Typography>
              <IsolatedInput 
                field="job_position"
                value={formData.job_position || ''}
                onChange={onInputChange}
                placeholder="e.g., Sales Manager"
                disabled={!canEditWorkInformation()}
                style={{ opacity: canEditWorkInformation() ? 1 : 0.6, cursor: canEditWorkInformation() ? 'text' : 'not-allowed' }}
              />
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
              <Typography sx={{ fontWeight: 500, width: 120, color: 'text.primary' }}>
                Email
              </Typography>
              <IsolatedInput 
                field="email"
                value={formData.email || ''}
                onChange={onInputChange}
                placeholder="john.doe@company.com"
                type="email"
                disabled={!canEditWorkInformation()}
                style={{ opacity: canEditWorkInformation() ? 1 : 0.6, cursor: canEditWorkInformation() ? 'text' : 'not-allowed' }}
              />
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
              <Typography sx={{ fontWeight: 500, width: 120, color: 'text.primary' }}>
                Work Phone
              </Typography>
              <IsolatedInput 
                field="work_phone"
                value={formData.work_phone || ''}
                onChange={onInputChange}
                placeholder="+1 (555) 123-4567"
                type="tel"
                disabled={!canEditWorkInformation()}
                style={{ opacity: canEditWorkInformation() ? 1 : 0.6, cursor: canEditWorkInformation() ? 'text' : 'not-allowed' }}
              />
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
              <Typography sx={{ fontWeight: 500, width: 120, color: 'text.primary' }}>
                Manager
              </Typography>
              <IsolatedInput 
                field="manager_name"
                value={formData.manager_name || ''}
                onChange={onInputChange}
                placeholder="Manager name"
                disabled={!canEditWorkInformation()}
                style={{ opacity: canEditWorkInformation() ? 1 : 0.6, cursor: canEditWorkInformation() ? 'text' : 'not-allowed' }}
              />
            </Box>
          </Grid>
        </Grid>
        </CardContent>
      </Card>

      {/* Location Section */}
      <Card sx={{
        mb: 4,
        borderRadius: 3,
        border: `1px solid rgba(0, 0, 0, 0.08)`,
        background: 'white',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.06)',

      }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Box sx={{ 
              p: 1.5, 
              borderRadius: 2, 
              background: 'linear-gradient(rgba(25, 118, 210, 0.1), rgba(25, 118, 210, 0.05))',
              border: '1px solid rgba(25, 118, 210, 0.2)'
            }}>
              <LocationOnIcon sx={{ fontSize: 24, color: 'primary.main' }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 500, color: 'primary.main' }}>
              Work Location
            </Typography>
          </Box>
        
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
              <Typography sx={{ fontWeight: 500, width: 120, color: 'text.primary' }}>
                Work Location
              </Typography>
              <FormControl fullWidth size="small" variant="outlined">
                <Select
                  value={formData.work_location || ''}
                  onChange={(e) => canEditWorkInformation() ? onInputChange('work_location', e.target.value) : {}}
                  displayEmpty
                  disabled={!canEditWorkInformation()}
                  disableScrollLock={true}
                  disablePortal={false}
                  disableAutoFocus={true}
                  disableEnforceFocus={true}
                  MenuProps={{
                    disableScrollLock: true,
                    disablePortal: false,
                    disableAutoFocus: true,
                    disableEnforceFocus: true,
                    PaperProps: {
                      sx: {
                        position: 'absolute !important',
                        contain: 'layout style paint',
                        zIndex: 9999,
                        borderRadius: 2,
                        boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
                      }
                    },
                    sx: {
                      zIndex: 9999
                    }
                  }}
                  sx={{ 
                    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                    '& .MuiSelect-select': { py: 0.5 }
                  }}
                >
                  <MenuItem value="">
                    <em style={{ color: '#999' }}>Select Location</em>
                  </MenuItem>
                  {WORK_LOCATIONS.map((location) => (
                    <MenuItem key={location} value={location}>
                      {location}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Grid>
          
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
              <Typography sx={{ fontWeight: 500, width: 120, color: 'text.primary' }}>
                Street Address
              </Typography>
              <IsolatedInput 
                field="work_street_address"
                value={formData.work_street_address || ''}
                onChange={onInputChange}
                disabled={!canEditWorkInformation()}
                style={{ opacity: canEditWorkInformation() ? 1 : 0.6, cursor: canEditWorkInformation() ? 'text' : 'not-allowed' }}
              />
            </Box>
          </Grid>
          
          
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
              <Typography sx={{ fontWeight: 500, width: 120, color: 'text.primary' }}>
                City
              </Typography>
              <IsolatedInput 
                field="work_city"
                value={formData.work_city || ''}
                onChange={onInputChange}
                disabled={!canEditWorkInformation()}
                style={{ opacity: canEditWorkInformation() ? 1 : 0.6, cursor: canEditWorkInformation() ? 'text' : 'not-allowed' }}
              />
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
              <Typography sx={{ fontWeight: 500, width: 120, color: 'text.primary' }}>
                State
              </Typography>
              <IsolatedInput 
                field="work_state"
                value={formData.work_state || ''}
                onChange={onInputChange}
                disabled={!canEditWorkInformation()}
                style={{ opacity: canEditWorkInformation() ? 1 : 0.6, cursor: canEditWorkInformation() ? 'text' : 'not-allowed' }}
              />
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
              <Typography sx={{ fontWeight: 500, width: 120, color: 'text.primary' }}>
                Country
              </Typography>
              <IsolatedInput 
                field="work_country"
                value={formData.work_country || ''}
                onChange={onInputChange}
                disabled={!canEditWorkInformation()}
                style={{ opacity: canEditWorkInformation() ? 1 : 0.6, cursor: canEditWorkInformation() ? 'text' : 'not-allowed' }}
              />
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
              <Typography sx={{ fontWeight: 500, width: 120, color: 'text.primary' }}>
                Zip Code
              </Typography>
              <IsolatedInput 
                field="work_zip_code"
                value={formData.work_zip_code || ''}
                onChange={onInputChange}
                disabled={!canEditWorkInformation()}
                style={{ opacity: canEditWorkInformation() ? 1 : 0.6, cursor: canEditWorkInformation() ? 'text' : 'not-allowed' }}
              />
            </Box>
          </Grid>
        </Grid>
        </CardContent>
      </Card>

      {/* Employment Details Section */}
      <Card sx={{
        mb: 4,
        borderRadius: 3,
        border: `1px solid rgba(0, 0, 0, 0.08)`,
        background: 'white',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.06)',

      }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Box sx={{ 
              p: 1.5, 
              borderRadius: 2, 
              background: 'linear-gradient(rgba(25, 118, 210, 0.1), rgba(25, 118, 210, 0.05))',
              border: '1px solid rgba(25, 118, 210, 0.2)'
            }}>
              <BusinessIcon sx={{ fontSize: 24, color: 'primary.main' }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 500, color: 'primary.main' }}>
              Employment Details
            </Typography>
          </Box>
        
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
              <Typography sx={{ fontWeight: 500, width: 120, color: 'text.primary' }}>
                Hire Date
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={formData.hire_date ? dayjs(formData.hire_date) : null}
                  onChange={(newValue) => {
                    if (canEditWorkInformation()) {
                      onInputChange('hire_date', newValue ? newValue.format('YYYY-MM-DD') : '');
                    }
                  }}
                  disabled={!canEditWorkInformation()}
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true,
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          border: 'none',
                          '& .MuiOutlinedInput-notchedOutline': { border: 'none' }
                        }
                      }
                    }
                  }}
                />
              </LocalizationProvider>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
              <Typography sx={{ fontWeight: 500, width: 120, color: 'text.primary' }}>
                Employment Type
              </Typography>
              <FormControl fullWidth size="small" variant="outlined">
                <Select
                  value={formData.employment_type}
                  onChange={(e) => canEditWorkInformation() ? onInputChange('employment_type', e.target.value) : {}}
                  displayEmpty
                  disabled={!canEditWorkInformation()}
                  disableScrollLock={true}
                  disablePortal={false}
                  disableAutoFocus={true}
                  disableEnforceFocus={true}
                  MenuProps={{
                    disableScrollLock: true,
                    disablePortal: false,
                    disableAutoFocus: true,
                    disableEnforceFocus: true,
                    PaperProps: {
                      sx: {
                        position: 'absolute !important',
                        contain: 'layout style paint',
                        zIndex: 9999,
                        borderRadius: 2,
                        boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
                      }
                    },
                    sx: {
                      zIndex: 9999
                    }
                  }}
                  sx={{ 
                    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                    '& .MuiSelect-select': { py: 0.5 }
                  }}
                >
                  {EMPLOYMENT_TYPES.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type.charAt(0).toUpperCase() + type.slice(1)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
              <Typography sx={{ fontWeight: 500, width: 120, color: 'text.primary' }}>
                Contract Start Date
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={formData.contract_start_date ? dayjs(formData.contract_start_date) : null}
                  onChange={(newValue) => {
                    if (canEditWorkInformation()) {
                      onInputChange('contract_start_date', newValue ? newValue.format('YYYY-MM-DD') : '');
                    }
                  }}
                  disabled={!canEditWorkInformation()}
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true,
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          border: 'none',
                          '& .MuiOutlinedInput-notchedOutline': { border: 'none' }
                        }
                      }
                    }
                  }}
                />
              </LocalizationProvider>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
              <Typography sx={{ fontWeight: 500, width: 120, color: 'text.primary' }}>
                Contract End Date
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  value={formData.contract_end_date ? dayjs(formData.contract_end_date) : null}
                  onChange={(newValue) => {
                    if (canEditWorkInformation()) {
                      onInputChange('contract_end_date', newValue ? newValue.format('YYYY-MM-DD') : '');
                    }
                  }}
                  disabled={!canEditWorkInformation()}
                  slotProps={{
                    textField: {
                      size: 'small',
                      fullWidth: true,
                      placeholder: 'Leave empty for indefinite contract',
                      sx: {
                        '& .MuiOutlinedInput-root': {
                          border: 'none',
                          '& .MuiOutlinedInput-notchedOutline': { border: 'none' }
                        }
                      }
                    }
                  }}
                />
              </LocalizationProvider>
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
              <Typography sx={{ fontWeight: 500, width: 120, color: 'text.primary' }}>
                Hourly Wage
              </Typography>
              <IsolatedInput 
                field="hourly_wage"
                value={formData.hourly_wage || ''}
                onChange={onInputChange}
                type="number"
                placeholder="$"
                disabled={!canEditWorkInformation()}
                style={{ opacity: canEditWorkInformation() ? 1 : 0.6, cursor: canEditWorkInformation() ? 'text' : 'not-allowed' }}
              />
            </Box>
          </Grid>
          
          <Grid item xs={12} md={6}>
            <Box sx={{ display: 'flex', alignItems: 'center', py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
              <Typography sx={{ fontWeight: 500, width: 120, color: 'text.primary' }}>
                Currency
              </Typography>
              <FormControl fullWidth size="small" variant="outlined">
                <Select
                  value={formData.currency}
                  onChange={(e) => canEditWorkInformation() ? onInputChange('currency', e.target.value) : {}}
                  displayEmpty
                  disabled={!canEditWorkInformation()}
                  disableScrollLock={true}
                  disablePortal={false}
                  disableAutoFocus={true}
                  disableEnforceFocus={true}
                  MenuProps={{
                    disableScrollLock: true,
                    disablePortal: false,
                    disableAutoFocus: true,
                    disableEnforceFocus: true,
                    PaperProps: {
                      sx: {
                        position: 'absolute !important',
                        contain: 'layout style paint',
                        zIndex: 9999,
                        borderRadius: 2,
                        boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
                      }
                    },
                    sx: {
                      zIndex: 9999
                    }
                  }}
                  sx={{ 
                    '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                    '& .MuiSelect-select': { py: 0.5 }
                  }}
                >
                  <MenuItem value="USD">USD</MenuItem>
                  <MenuItem value="CAD">CAD</MenuItem>
                  <MenuItem value="EUR">EUR</MenuItem>
                  <MenuItem value="GBP">GBP</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Grid>
        </Grid>
        </CardContent>
      </Card>

      {/* Remote Work Section */}
      <Card sx={{
        mb: 4,
        borderRadius: 3,
        border: `1px solid rgba(0, 0, 0, 0.08)`,
        background: 'white',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.06)',

      }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Box sx={{ 
              p: 1.5, 
              borderRadius: 2, 
              background: 'linear-gradient(rgba(25, 118, 210, 0.1), rgba(25, 118, 210, 0.05))',
              border: '1px solid rgba(25, 118, 210, 0.2)'
            }}>
              <ScheduleIcon sx={{ fontSize: 24, color: 'primary.main' }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 500, color: 'primary.main' }}>
              Remote Work Schedule
            </Typography>
          </Box>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Specify your default work location for each day of the week. This schedule will repeat itself each week.
        </Typography>
        
        <Grid container spacing={0}>
          {DAYS_OF_WEEK.map((dayName, index) => {
            const dayData = remoteSchedule.find(d => d.day_of_week === index) || { work_location: '', notes: '' };
            return (
              <Grid item xs={12} key={dayName}>
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  py: 1.5, 
                  borderBottom: '1px solid #f0f0f0',
                  gap: 2
                }}>
                  <Typography sx={{ fontWeight: 500, width: 100, color: 'text.primary' }}>
                    {dayName}
                  </Typography>
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <Select
                      value={dayData.work_location}
                      onChange={(e) => canEditWorkInformation() ? handleRemoteScheduleChange(index, 'work_location', e.target.value) : {}}
                      displayEmpty
                      disabled={!canEditWorkInformation()}
                      disableScrollLock={true}
                      disablePortal={false}
                      disableAutoFocus={true}
                      disableEnforceFocus={true}
                      MenuProps={{
                        disableScrollLock: true,
                        disablePortal: false,
                        disableAutoFocus: true,
                        disableEnforceFocus: true,
                        PaperProps: {
                          sx: {
                            position: 'absolute !important',
                            contain: 'layout style paint',
                            zIndex: 9999,
                            borderRadius: 2,
                            boxShadow: '0 8px 32px rgba(0,0,0,0.12)'
                          }
                        },
                        sx: {
                          zIndex: 9999
                        }
                      }}
                      sx={{ 
                        '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                        '& .MuiSelect-select': { py: 0.5 }
                      }}
                    >
                      <MenuItem value="">
                        <em style={{ color: '#999' }}>Unspecified</em>
                      </MenuItem>
                      {WORK_LOCATIONS.map((location) => (
                        <MenuItem key={location} value={location}>
                          {location}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Box>
              </Grid>
            );
          })}
        </Grid>
        </CardContent>
      </Card>

      {/* Job Description Section */}
      <Card sx={{
        mb: 4,
        borderRadius: 3,
        border: `1px solid rgba(0, 0, 0, 0.08)`,
        background: 'white',
        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.06)',
      }}>
        <CardContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <Box sx={{ 
              p: 1.5, 
              borderRadius: 2, 
              background: 'linear-gradient(rgba(25, 118, 210, 0.1), rgba(25, 118, 210, 0.05))',
              border: '1px solid rgba(25, 118, 210, 0.2)'
            }}>
              <WorkIcon sx={{ fontSize: 24, color: 'primary.main' }} />
            </Box>
            <Typography variant="h5" sx={{ fontWeight: 500, color: 'primary.main' }}>
              Job Description
            </Typography>
          </Box>
          
          <JobDescriptionMarkdown
            value={localJobDescription}
            onChange={setLocalJobDescription}
            onBlur={() => {
              if (localJobDescription !== formData.job_description) {
                onInputChange('job_description', localJobDescription);
              }
            }}
            disabled={!canEditWorkInformation()}
          />
        </CardContent>
      </Card>

      {/* Department Selection Dialog */}
      <Dialog
        open={departmentSelectionDialogOpen}
        onClose={handleCloseDepartmentSelectionDialog}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 4,
            height: '40vh',
            width: '600px',
            maxWidth: '600px',
            margin: 'auto',
            border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
            boxShadow: '0 24px 48px rgba(0,0,0,0.15)'
          }
        }}
      >
        <DialogTitle sx={{ 
          pb: 2, 
          fontSize: '1.25rem', 
          fontWeight: 500,
          color: 'text.primary',
          borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <Typography variant="h6" component="span" sx={{ fontWeight: 500, fontSize: '1.1rem' }}>
            Select Department
          </Typography>
          <IconButton onClick={handleCloseDepartmentSelectionDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ p: 3, borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}` }}>
            <TextField
              fullWidth
              placeholder="Search departments..."
              value={departmentSearchTerm}
              onChange={(e) => setDepartmentSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: theme.palette.primary.main }} />
                  </InputAdornment>
                ),
                sx: { 
                  borderRadius: 3,
                  border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                  background: 'white',
                  '&:hover': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`
                  },
                  '&.Mui-focused': {
                    border: `1px solid ${theme.palette.primary.main}`,
                    boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.25)}`
                  }
                }
              }}
              size="medium"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': {
                    border: 'none'
                  },
                  '&:hover fieldset': {
                    border: 'none'
                  },
                  '&.Mui-focused fieldset': {
                    border: 'none'
                  }
                }
              }}
            />
          </Box>
          
          <Box sx={{ 
            flex: 1,
            overflowY: 'auto',
            '&::-webkit-scrollbar': {
              display: 'none'
            },
            scrollbarWidth: 'none',
            msOverflowStyle: 'none'
          }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 500, py: 2, pl: 3 }}>Department</TableCell>
                  <TableCell sx={{ fontWeight: 500, py: 2 }} align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {getFilteredDepartmentsForSelection().map((department) => (
                  <TableRow 
                    key={department.id}
                    hover
                    sx={{
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        bgcolor: 'grey.50',
                        transform: 'translateX(4px)',
                        boxShadow: 'inset 4px 0 0 black'
                      },
                      '& .MuiTableCell-root': {
                        borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.6)}`,
                        py: 2,
                        pl: 3,
                        pr: 3
                      }
                    }}
                  >
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.9rem' }}>
                          {department.displayName}
                        </Typography>
                        {department.description && (
                          <Typography variant="caption" color="text.secondary">
                            {department.description}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleDialogDepartmentSelect(department)}
                        sx={{
                          borderRadius: 2,
                          textTransform: 'none',
                          fontWeight: 500,
                          fontSize: '0.8rem',
                          px: 2,
                          py: 1,
                          border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                          transition: 'all 0.2s cubic-bezier(0.4, 0.0, 0.2, 1)',
                          bgcolor: dialogSelectedDepartment?.id === department.id ? 'black' : 'transparent',
                          color: dialogSelectedDepartment?.id === department.id ? 'white' : 'inherit',
                          '&:hover': {
                            bgcolor: dialogSelectedDepartment?.id === department.id ? 'black' : 'transparent',
                            border: `1px solid ${theme.palette.primary.main}`,
                            transform: 'translateY(-1px)',
                            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`
                          }
                        }}
                      >
                        Select
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {getFilteredDepartmentsForSelection().length === 0 && (
                  <TableRow>
                    <TableCell colSpan={2} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        {departmentSearchTerm ? 'No departments found matching your search.' : 'No departments available.'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
        </DialogContent>
        <DialogActions sx={{ 
          p: 3, 
          gap: 2, 
          borderTop: `1px solid ${alpha(theme.palette.grey[300], 0.8)}`,
          bgcolor: alpha(theme.palette.grey[50], 0.5),
          display: 'flex',
          justifyContent: 'space-between'
        }}>
          <Button
            onClick={() => {
              window.open(`/company/${companyId}/employees#department-manager`, '_blank');
            }}
            variant="outlined"
            startIcon={<AddIcon />}
            sx={{ 
              borderRadius: 2, 
              px: 3, 
              py: 1, 
              fontSize: '0.9rem', 
              fontWeight: 500,
              textTransform: 'none',
              border: `1px solid ${alpha(theme.palette.success.main, 0.8)}`,
              color: 'success.main',
              '&:hover': {
                border: `1px solid ${theme.palette.success.main}`,
                bgcolor: alpha(theme.palette.success.main, 0.1),
                transform: 'translateY(-1px)'
              }
            }}
          >
            Add Department
          </Button>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              onClick={handleCloseDepartmentSelectionDialog}
              variant="outlined"
              sx={{ 
                borderRadius: 2, 
                px: 3, 
                py: 1, 
                fontSize: '0.9rem', 
                fontWeight: 500,
                textTransform: 'none',
                border: `1px solid ${alpha(theme.palette.grey[400], 0.8)}`,
                '&:hover': {
                  border: `1px solid ${theme.palette.grey[600]}`,
                  transform: 'translateY(-1px)'
                }
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleConfirmDepartmentSelection}
              variant="contained"
              disabled={!dialogSelectedDepartment}
              sx={{ 
                borderRadius: 2, 
                px: 3, 
                py: 1, 
                fontSize: '0.9rem', 
                fontWeight: 500,
                textTransform: 'none',
                background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
                '&:hover': {
                  background: `linear-gradient(135deg, ${theme.palette.primary.dark}, ${theme.palette.primary.main})`,
                  transform: 'translateY(-1px)',
                  boxShadow: `0 6px 20px ${alpha(theme.palette.primary.main, 0.3)}`
                },
                '&:disabled': {
                  background: alpha(theme.palette.grey[400], 0.5),
                  color: alpha(theme.palette.grey[600], 0.7)
                }
              }}
            >
              Confirm Selection
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
    </Box>
  );
}